import {Component, ElementRef, Inject, LOCALE_ID, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Role} from '../../model/auth/Role';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {CompetitionService} from '../../service/competition.service';
import {Competition} from '../../model/competition/Competition';
import {ProgressService} from '../../service/progress.service';
import {BaseModalComponent} from '../../common/modal/base-modal/base-modal.component';
import {AuthService} from '../../service/auth.service';
import {Session} from '../../model/auth/Session';
import {NotificationHandler} from '../../helper/notification.handler';
import {Observable} from 'rxjs';
import {EditorComponent} from '@tinymce/tinymce-angular';
import {EditorConfigHelper} from '../../helper/editor-config.helper';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';

export interface CompetitionDetailsData {
  competition?: Competition;
}

@Component({
  selector: 'app-competition-details-modal',
  templateUrl: './competition-details-modal.component.html',
  styleUrls: ['./competition-details-modal.component.scss']
})
export class CompetitionDetailsModalComponent extends BaseModalComponent {

  readonly roles = [Role.Admin, Role.Host];

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;
  @ViewChild('editorComponent', {static: true}) editor!: EditorComponent;

  nameField = new FormControl({value: '', disabled: true}, [
    Validators.required, Validators.minLength(3), Validators.maxLength(128)
  ]);
  domainField = new FormControl({value: '', disabled: true}, [
    Validators.required, Validators.minLength(3), Validators.maxLength(16), Validators.pattern('[a-z]+[a-z0-9]*')
  ]);
  descriptionField = new FormControl('', [
    Validators.maxLength(5120)
  ]);
  publishedField = new FormControl({value: '', disabled: true}, [
    Validators.required
  ]);
  detailsForm = new FormGroup({
    name: this.nameField,
    domain: this.domainField,
    description: this.descriptionField,
    published: this.publishedField,
  });

  private session!: Session;

  readonly competitionId?: string;
  private competition?: Competition;
  action: string;

  initEditor: boolean = false;
  editorConfig: object;

  constructor(private competitionService: CompetitionService,
              private authService: AuthService,
              dialogRef: MatDialogRef<CompetitionDetailsModalComponent>,
              progressService: ProgressService,
              breakpointObserver: BreakpointObserver,
              @Inject(LOCALE_ID) locale: string,
              @Inject(MAT_DIALOG_DATA) data: CompetitionDetailsData) {
    super(dialogRef, progressService);

    if (data && data.competition && data.competition.id) {
      this.action = $localize`:General|Edit action label@@general-edit-action-label:Edit`;
      this.competitionId = data.competition.id;
    } else {
      this.action = $localize`:General|Create action label@@general-create-action-label:Create`;
    }

    const isMobile = breakpointObserver.isMatched(Breakpoints.HandsetPortrait);
    const descriptionPlaceholder = $localize`:Competitions Details Modal|Competitions Details modal description input placeholder@@competitions-details-modal-description-input-placeholder:Competition description...`;
    this.editorConfig = EditorConfigHelper.config(locale, descriptionPlaceholder, !isMobile);
  }

  async initContent() {
    this.session = await this.authService.getSession();

    if (this.competitionId) {
      this.loadCompetition(this.competitionId);
    } else if (this.session.isAdmin()) {
      this.nameField.enable();
      this.domainField.enable();
      this.publishedField.enable();
    }
  }

  get editorApiKey() {
    return EditorConfigHelper.editorApiKey;
  }

  private loadCompetition(id: string) {
    this.startProgress();

    this.competitionService.getDetails(id).subscribe(competition => {
      this.competition = competition;

      if (this.session.isAdmin()) {
        this.descriptionField.disable();
        this.nameField.enable();
        this.domainField.enable();
        this.publishedField.enable();
      }

      this.nameField.setValue(this.competition.name);
      this.domainField.setValue(this.competition.domain);
      this.descriptionField.setValue(this.competition.description);
      this.publishedField.setValue(this.competition.published);

      this.initEditor = true;

      this.stopProgress();
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Competition details fetch has failed message@@notifications-competition-details-fetch-failed-error:Competition details fetch has failed`, error);
      this.stopProgress();
    });
  }

  proceedAllowed(): boolean {
    return this.detailsForm.valid;
  }

  proceed(): void {
    this.startProgress();

    let observable: Observable<Competition>;

    const name = this.nameField.value as string;
    const domain = this.domainField.value as string;
    const description = this.descriptionField.value as string;
    const published = this.publishedField.value as boolean;

    if (this.competition) {
      observable = this.competitionService.update(this.competition.id, name, domain, description, published);
    } else {
      observable = this.competitionService.create(name, domain);
    }

    observable.subscribe(competition => {
      this.stopProgress();
      const data: CompetitionDetailsData = {
        competition
      };
      this.closeModal(data);
    }, error => {
      this.stopProgress();

      if (this.competition) {
        NotificationHandler.handleError($localize`:Notifications|Competition details update has failed message@@notifications-competition-details-update-failed-error:Competition details update has failed`, error);
      } else {
        NotificationHandler.handleError($localize`:Notifications|Competition create has failed message@@notifications-competition-create-failed-error:Competition create has failed`, error);
      }
    });
  }

}
